import React, { useMemo } from 'react';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../SubscriptionProvider';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

const SubscriptionExpirationWarningButton: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  const { t, i18n } = useTranslation();
  const { expirationWarning } = useSubscription();

  const formattedDate = useMemo(() => {
    if (!expirationWarning?.endsAtUtc) {
      return '';
    }

    const date = expirationWarning.endsAtUtc instanceof Date
      ? expirationWarning.endsAtUtc
      : new Date(expirationWarning.endsAtUtc);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language;
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }, [expirationWarning?.endsAtUtc, i18n.language]);

  if (expirationWarning?.isEnabled !== true) {
    return null;
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size={compact ? 'sm' : 'icon-sm'}
          className={compact
            ? 'h-7 border-amber-300 bg-amber-50 px-2 text-amber-700 hover:bg-amber-100'
            : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'}
          aria-label={t('subscriptionExpirationWarningButtonLabel')}
          title={t('subscriptionExpirationWarningButtonLabel')}
        >
          <TriangleAlert className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm rounded-lg border-amber-200 bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-800">
            <TriangleAlert className="h-5 w-5" />
            {t('subscriptionExpirationWarningTitle')}
          </DialogTitle>
          <DialogDescription className="text-left text-sm leading-6 text-slate-700">
            {t('subscriptionExpirationWarningMessage', {
              count: expirationWarning.daysRemaining ?? 0,
              days: expirationWarning.daysRemaining ?? 0,
              date: formattedDate,
            })}
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionExpirationWarningButton;
